import type { ApiErrorBody, ErrorCode } from '@terra/contracts';

/**
 * The single error type every API call rejects with. Wraps the server's error envelope when present;
 * synthesises a sensible one for network/timeout/parse failures.
 */
export class ApiClientError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR' | 'TIMEOUT' | 'CANCELLED' | 'UNKNOWN';
  readonly status: number; // 0 when there was no HTTP response
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(args: {
    code: ApiClientError['code'];
    message: string;
    status: number;
    details?: unknown;
    requestId?: string;
  }) {
    super(args.message);
    this.name = 'ApiClientError';
    this.code = args.code;
    this.status = args.status;
    if (args.details !== undefined) this.details = args.details;
    if (args.requestId !== undefined) this.requestId = args.requestId;
  }

  /** True for errors that are safe to auto-retry (network blips, 502/503/504). */
  get isRetryable(): boolean {
    return (
      this.code === 'NETWORK_ERROR' ||
      this.code === 'TIMEOUT' ||
      this.status === 502 ||
      this.status === 503 ||
      this.status === 504
    );
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error?: unknown }).error === 'object'
  );
}
